/**
 * DLE 評価アンケートの回答モーダル。
 *
 * **1 ページ 1 問**で進める。デザイナー貢献度アンケート（8 軸を 1 画面に積む）と違い、
 * DLE 評価は 1 問あたり設問文 + 目的 + 5 段階の長い説明 + コメント欄を持つため、
 * 1 画面に 4 問並べると縦に流れて選択肢の説明が読まれない。1 問ずつ出し、
 * 何問目かと残りを常に見せる（進捗が見えないと途中で離脱する）。
 *
 * 1 モーダル = 1 母票。同じモーダルに複数の母票を渡すと、送信後そのまま次の案件へ進む。
 *
 * 配色は開発 view のアクセント（ライト = 青 / ダーク = 黄）に合わせる。テーブル側の
 * 「回答する」ボタンと同じ色でないと、どの列から来たモーダルなのか分からない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business DLE 評価アンケートの回答モーダル（4 問を 1 ページ 1 問で進め、各問に評点 + コメント）
 * @graph-connects useSubmitDleAnswer [calls] DLE 評価回答登録
 * @graph-connects SurveyScaleBar [calls] 評点の目盛バー
 */
import { useState } from 'react';

import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';

import { useSubmitDleAnswer } from '../../../hooks/useDleEvaluationSurvey.js';
import {
  DLE_NOT_APPLICABLE_NOTE,
  DLE_QUESTIONS,
  DLE_SCALE_OVERVIEW,
  DLE_SCORES,
  type DleAnswerValue,
  type DleEvaluationTask,
  type DleScore,
} from '../../../types/dle-evaluation.js';
import { SurveyScaleBar } from './SurveyScaleBar.js';

interface DleSurveyModalProps {
  /** 未回答タスク（母票単位）。先頭から順に回答していく */
  tasks: DleEvaluationTask[];
  open: boolean;
  onClose: () => void;
}

/** 開発 view のアクセント色（ライト = 青 / ダーク = 黄）。 @graph-connects none */
const DEV_ACCENT = 'var(--color-dev-accent)';

/** 「該当なし」を目盛バーの外側チップに出すときの表示。 @graph-connects none */
const NOT_APPLICABLE_LABEL = '該当なし';

/**
 * 目盛バーは `string` の尺度を取るので、評点を文字列に写して渡す。
 * 文字列のまま state に持つと「3」と 3 の往復変換が散らばるので、境界はここだけに閉じる。
 * @graph-connects none
 */
const SCORE_SCALE: readonly string[] = DLE_SCORES.map(String);

/** 目盛バーの選択値 → 評点 / 該当なし。 @graph-connects none */
function toAnswerValue(raw: string): DleAnswerValue {
  if (raw === NOT_APPLICABLE_LABEL) return null;
  return Number(raw) as DleScore;
}

/**
 * 見出しの文字色。素のアクセント色はライトテーマの白地でコントラストが足りないので
 * **本文色と混ぜて**使う。`--color-text-primary` はダークで白寄り / ライトで黒寄りなので、
 * 同じ式のままダークでは明るく・ライトでは暗く転び、両テーマで可読性が確保される。
 * @graph-connects none
 */
function headingColor(color: string): string {
  return `color-mix(in srgb, ${color} 55%, var(--color-text-primary))`;
}

/**
 * 1 問ぶんの進捗ドット。現在位置と、回答済み / 未回答が形で分かる。
 *
 * ドットにしてあるのは **4 問しかないから**。数字だけ（3/4 問）だと「あと何問か」は
 * 分かっても「どこを飛ばしたか」が分からず、戻るボタンを押す判断ができない。
 * @graph-connects none
 */
function ProgressDots({
  total,
  current,
  answered,
}: {
  total: number;
  current: number;
  answered: readonly boolean[];
}) {
  return (
    <div className="flex items-center gap-1.5" aria-hidden>
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className="h-1.5 rounded-full transition-all"
          style={{
            width: i === current ? '1.5rem' : '0.75rem',
            background:
              i === current
                ? DEV_ACCENT
                : answered[i]
                  ? `color-mix(in srgb, ${DEV_ACCENT} 45%, transparent)`
                  : 'var(--color-border)',
          }}
        />
      ))}
    </div>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business DLE 評価アンケート本体。4 問を 1 ページ 1 問で進め、最終問で確認 → 送信し、次の案件へ移る
 * @graph-connects useSubmitDleAnswer [calls] 回答登録ミューテーション
 * @graph-connects SurveyScaleBar [calls] 評点の目盛バー
 */
export function DleSurveyModal({ tasks, open, onClose }: DleSurveyModalProps) {
  const [taskIndex, setTaskIndex] = useState(0);
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState<DleAnswerValue[]>(DLE_QUESTIONS.map(() => null));
  /** 「まだ触っていない」と「該当なしを選んだ」を区別する。評点は null が有効値なので別で持つ */
  const [touched, setTouched] = useState<boolean[]>(DLE_QUESTIONS.map(() => false));
  const [comments, setComments] = useState<string[]>(DLE_QUESTIONS.map(() => ''));
  const [confirming, setConfirming] = useState(false);

  const submit = useSubmitDleAnswer();
  const task = tasks[taskIndex];
  const question = DLE_QUESTIONS[step];
  const isLast = step === DLE_QUESTIONS.length - 1;

  /** 次の案件へ進む（最後なら閉じる）。入力状態は毎回リセットする。 */
  const goNextTask = () => {
    setScores(DLE_QUESTIONS.map(() => null));
    setTouched(DLE_QUESTIONS.map(() => false));
    setComments(DLE_QUESTIONS.map(() => ''));
    setStep(0);
    setConfirming(false);
    submit.reset();
    if (taskIndex + 1 < tasks.length) {
      setTaskIndex(taskIndex + 1);
    } else {
      setTaskIndex(0);
      onClose();
    }
  };

  if (!task || !question) return null;

  const allAnswered = touched.every(Boolean);
  /** いま出している設問に評点が付いているか。`該当なし` は null が有効値なので touched で見る。 */
  const answeredHere = touched[step] ?? false;

  const handleSubmit = () => {
    if (!allAnswered) return;
    submit.mutate(
      {
        parentIssueKey: task.parentIssueKey,
        scores,
        comments: comments.map((c) => (c.trim() === '' ? null : c.trim())),
      },
      { onSuccess: goNextTask },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="full"
      title={
        <div className="flex flex-wrap items-center gap-2">
          <span>DLE 評価アンケート</span>
          <Badge variant="info" size="sm">
            {task.dle}
          </Badge>
          {tasks.length > 1 && (
            <Badge variant="default" size="sm">
              案件 {taskIndex + 1}/{tasks.length} 件
            </Badge>
          )}
        </div>
      }
      footer={
        <div className="flex w-full flex-col gap-2">
          <p className="text-xs text-[var(--color-warning-text)]">
            ⚠ 回答は 1 案件につき 1 回のみです。送信後は編集できません。
          </p>
          {submit.isError && (
            <p className="text-xs text-[var(--color-error-text)]">
              送信に失敗しました。時間をおいて再度お試しください。
            </p>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2">
            {/* 進捗は footer にも数字で置く。本文をスクロールしても残り問数が見える。 */}
            <span className="text-xs text-[var(--color-text-muted)]">
              設問 {step + 1} / {DLE_QUESTIONS.length}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={
                  step === 0
                    ? onClose
                    : () => {
                        setStep(step - 1);
                        setConfirming(false);
                      }
                }
                disabled={submit.isPending}
              >
                {step === 0 ? 'キャンセル' : '戻る'}
              </Button>
              {!isLast ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    setStep(step + 1);
                  }}
                  disabled={!touched[step]}
                >
                  次の設問へ
                </Button>
              ) : confirming ? (
                <Button variant="primary" onClick={handleSubmit} disabled={submit.isPending}>
                  {submit.isPending ? '送信中…' : 'この内容で送信する'}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => {
                    setConfirming(true);
                  }}
                  disabled={!allAnswered}
                >
                  送信する
                </Button>
              )}
            </div>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <section className="rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
          <p className="font-medium text-[var(--color-text-primary)]">{task.parentSummary}</p>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {task.parentIssueKey} ／ DLE {task.dle} ／ {task.parentStatus} ／ 回答可能になった日{' '}
            {task.eligibleSince}
          </p>
        </section>

        {/* 共通の評価基準は 1 問目だけ。2 問目以降は設問固有の 5 段階説明が同じ場所に出る。 */}
        {step === 0 && (
          <section className="rounded border border-[var(--color-border)] p-3">
            <h3 className="mb-1.5 text-sm font-bold text-[var(--color-text-secondary)]">
              共通の評価基準
            </h3>
            <ul className="space-y-0.5 text-xs text-[var(--color-text-muted)]">
              {DLE_SCORES.map((score) => (
                <li key={score}>
                  <span className="font-semibold text-[var(--color-text-secondary)]">{score}</span>{' '}
                  {DLE_SCALE_OVERVIEW[score]}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section
          className="rounded border border-l-[3px] border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4"
          style={{ borderLeftColor: DEV_ACCENT }}
        >
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <ProgressDots total={DLE_QUESTIONS.length} current={step} answered={touched} />
            <Badge variant={answeredHere ? 'success' : 'warning'} size="sm">
              {answeredHere ? '回答済' : '必須'}
            </Badge>
          </div>

          <h3 className="text-base font-bold" style={{ color: headingColor(DEV_ACCENT) }}>
            Q{step + 1}. {question.title}
          </h3>
          <p className="mt-1.5 text-xs leading-relaxed text-[var(--color-text-muted)]">
            {question.purpose}
          </p>

          <div className="mt-4">
            <SurveyScaleBar
              scale={SCORE_SCALE}
              offScale={question.allowsNotApplicable ? NOT_APPLICABLE_LABEL : undefined}
              value={answeredHere ? (scores[step]?.toString() ?? NOT_APPLICABLE_LABEL) : null}
              ariaLabel={question.title}
              color={DEV_ACCENT}
              onChange={(next) => {
                setScores(scores.map((v, i) => (i === step ? toAnswerValue(next) : v)));
                setTouched(touched.map((v, i) => (i === step ? true : v)));
              }}
            />
            {question.allowsNotApplicable && (
              <p className="mt-1.5 text-[11px] text-[var(--color-text-muted)]">
                {NOT_APPLICABLE_LABEL}: {DLE_NOT_APPLICABLE_NOTE}
              </p>
            )}
          </div>

          {/* 選んだ段の意味を 1 行で返す。バーのラベルは数字しか出ないので、
              選択の直後に「その点が何を指すか」を確認できるようにする。 */}
          <ul className="mt-3 space-y-0.5 text-xs">
            {DLE_SCORES.map((score) => {
              const selected = answeredHere && scores[step] === score;
              return (
                <li
                  key={score}
                  style={{
                    color: selected ? headingColor(DEV_ACCENT) : 'var(--color-text-muted)',
                    fontWeight: selected ? 600 : 400,
                  }}
                >
                  <span className="font-semibold">{score}</span>：{question.scaleLabels[score]}
                </li>
              );
            })}
          </ul>

          <div className="mt-4">
            <label
              htmlFor={`dle-comment-${question.key}`}
              className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]"
            >
              {question.commentLabel}
              <span className="ml-1.5 font-normal text-[var(--color-text-muted)]">（任意）</span>
            </label>
            <textarea
              id={`dle-comment-${question.key}`}
              rows={4}
              value={comments[step] ?? ''}
              onChange={(e) => {
                setComments(comments.map((c, i) => (i === step ? e.target.value : c)));
              }}
              placeholder={question.commentPlaceholder}
              className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-base)] p-2 text-sm"
            />
          </div>
        </section>

        {/* 送信確認では全問の答えを 1 画面で見せる。1 問ずつ進む形式は
            「何を送ろうとしているか」を最後まで一覧できないため。 */}
        {confirming && (
          <section className="rounded border border-[var(--color-border)] p-3">
            <h3 className="mb-2 text-sm font-bold text-[var(--color-text-secondary)]">
              送信内容の確認
            </h3>
            <ul className="space-y-2 text-xs">
              {DLE_QUESTIONS.map((q, i) => (
                <li key={q.key} className="flex gap-2">
                  <span
                    className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
                    style={{ background: DEV_ACCENT, color: 'var(--color-dev-accent-on)' }}
                  >
                    {scores[i] ?? '—'}
                  </span>
                  <span className="min-w-0">
                    <span className="text-[var(--color-text-secondary)]">{q.title}</span>
                    {comments[i]?.trim() !== '' && (
                      <span className="block whitespace-pre-wrap text-[var(--color-text-muted)]">
                        {comments[i]}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </Modal>
  );
}
